import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, MessageCircle, User, Bot, Phone, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function generateSessionId() {
  return 'chat_' + Math.random().toString(36).substr(2, 12) + '_' + Date.now();
}

/** Render text with clickable links */
function MessageText({ text }) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) =>
        urlRegex.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800 break-all">
            {part.length > 50 ? 'View full details here' : part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function SalesChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem('cts_chat_session');
    if (stored) return stored;
    const id = generateSessionId();
    sessionStorage.setItem('cts_chat_session', id);
    return id;
  });

  // Lead capture state
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', email: '' });
  const [leadErrors, setLeadErrors] = useState({});
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);

  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showLeadForm]);

  // Load history on first open
  const historyLoaded = useRef(false);
  useEffect(() => {
    if (open && !historyLoaded.current) {
      historyLoaded.current = true;
      axios.get(`${API}/api/chatbot/history/${sessionId}`).then(res => {
        if (res.data && res.data.length > 0) {
          setMessages(res.data.map(m => ({ role: m.role, text: m.text })));
        } else {
          setMessages([{
            role: 'assistant',
            text: "Hi there! I'm the CTS sales assistant. I can help you find the perfect truck or equipment. What are you looking for today?"
          }]);
        }
      }).catch(() => {
        setMessages([{
          role: 'assistant',
          text: "Hi there! I'm the CTS sales assistant. I can help you find the perfect truck or equipment. What are you looking for today?"
        }]);
      });
    }
  }, [open, sessionId]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/chatbot/message`, {
        session_id: sessionId,
        message: text
      });
      const reply = res.data.response;
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);

      // Check if AI is asking for contact info — show lead form
      const lower = reply.toLowerCase();
      if (!leadCaptured && (
        lower.includes('phone number') ||
        lower.includes('callback') ||
        lower.includes('call you') ||
        lower.includes('reach out') ||
        lower.includes('get your name') ||
        lower.includes('contact info') ||
        lower.includes('your number')
      )) {
        setTimeout(() => setShowLeadForm(true), 600);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "Sorry, I'm having trouble connecting right now. Please call us at 1 (844) 417-2525." }]);
    }
    setLoading(false);
  }, [input, loading, sessionId, leadCaptured]);

  const validateLeadForm = () => {
    const errors = {};
    if (!leadForm.name.trim()) errors.name = 'Name is required';
    const phoneClean = leadForm.phone.replace(/[\s\-().+]/g, '');
    if (!phoneClean || phoneClean.length < 10 || !/^\d+$/.test(phoneClean)) {
      errors.phone = 'Enter a valid phone number (min 10 digits)';
    }
    if (leadForm.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(leadForm.email.trim())) {
        errors.email = 'Enter a valid email address';
      }
    }
    return errors;
  };

  const submitLead = async () => {
    const errors = validateLeadForm();
    setLeadErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setLeadSubmitting(true);

    // Build interest summary from chat
    const userMsgs = messages.filter(m => m.role === 'user').map(m => m.text);
    const interest = userMsgs.slice(-3).join(' | ');

    try {
      await axios.post(`${API}/api/chatbot/capture-lead`, {
        session_id: sessionId,
        name: leadForm.name,
        phone: leadForm.phone,
        email: leadForm.email,
        interest: interest,
      });
      setShowLeadForm(false);
      setLeadCaptured(true);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: `Thanks ${leadForm.name}! One of our specialists will call you shortly at ${leadForm.phone}. In the meantime, feel free to keep asking me about our inventory!`
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "Sorry, I couldn't submit your info. Please call us directly at 1 (844) 417-2525."
      }]);
    }
    setLeadSubmitting(false);
  };

  return (
    <>
      {/* FAB Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#E31937] rounded-full shadow-lg shadow-red-900/30 flex items-center justify-center hover:scale-110 transition-transform group"
          data-testid="chatbot-fab"
          aria-label="Open Sales Chat"
        >
          <MessageCircle className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-32px)] rounded-2xl shadow-2xl shadow-black/20 overflow-hidden flex flex-col bg-white border border-gray-200" style={{ height: '520px' }} data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-[#0A0A0A] px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-[#E31937] flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">CTS Sales Assistant</p>
              <p className="text-green-400 text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" /> Online
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1" data-testid="close-chatbot">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-[#E31937] flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#E31937] text-white rounded-br-sm'
                    : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                }`}>
                  <MessageText text={msg.text} />
                </div>
                {msg.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-3.5 h-3.5 text-gray-600" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start gap-2">
                <div className="w-6 h-6 rounded-full bg-[#E31937] flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-white text-gray-400 px-4 py-2.5 rounded-xl rounded-bl-sm shadow-sm border border-gray-100 text-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                  </div>
                </div>
              </div>
            )}

            {/* Lead Capture Form */}
            {showLeadForm && !leadCaptured && (
              <div className="bg-white border border-[#E31937]/20 rounded-xl p-3 shadow-sm space-y-2" data-testid="lead-capture-form">
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-[#E31937]" /> Request a Callback
                </p>
                <Input
                  placeholder="Your name"
                  value={leadForm.name}
                  onChange={e => { setLeadForm(f => ({ ...f, name: e.target.value })); setLeadErrors(e2 => ({...e2, name: undefined})); }}
                  className={`h-8 text-sm bg-gray-50 ${leadErrors.name ? 'border-red-400' : ''}`}
                  data-testid="lead-name-input"
                />
                {leadErrors.name && <p className="text-[10px] text-red-500 -mt-1">{leadErrors.name}</p>}
                <Input
                  placeholder="Phone number"
                  value={leadForm.phone}
                  onChange={e => { setLeadForm(f => ({ ...f, phone: e.target.value })); setLeadErrors(e2 => ({...e2, phone: undefined})); }}
                  className={`h-8 text-sm bg-gray-50 ${leadErrors.phone ? 'border-red-400' : ''}`}
                  data-testid="lead-phone-input"
                />
                {leadErrors.phone && <p className="text-[10px] text-red-500 -mt-1">{leadErrors.phone}</p>}
                <Input
                  placeholder="Email (optional)"
                  value={leadForm.email}
                  onChange={e => { setLeadForm(f => ({ ...f, email: e.target.value })); setLeadErrors(e2 => ({...e2, email: undefined})); }}
                  className={`h-8 text-sm bg-gray-50 ${leadErrors.email ? 'border-red-400' : ''}`}
                  data-testid="lead-email-input"
                />
                {leadErrors.email && <p className="text-[10px] text-red-500 -mt-1">{leadErrors.email}</p>}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 bg-[#E31937] hover:bg-[#C4122C] text-white h-8 text-xs"
                    onClick={submitLead}
                    disabled={leadSubmitting || !leadForm.name || !leadForm.phone}
                    data-testid="submit-lead-btn"
                  >
                    {leadSubmitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
                    {leadSubmitting ? 'Submitting...' : 'Request Callback'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-gray-400"
                    onClick={() => setShowLeadForm(false)}
                  >
                    Later
                  </Button>
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="bg-white border-t border-gray-200 px-3 py-2.5 flex items-center gap-2 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Ask about our trucks..."
              className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm outline-none border-0"
              disabled={loading}
              data-testid="chatbot-input"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="w-9 h-9 bg-[#E31937] rounded-full flex items-center justify-center hover:bg-[#C4122C] transition-colors disabled:opacity-40"
              data-testid="chatbot-send-btn"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
